import { useState } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import AdminProductCard from "./AdminProductCard";
import UpdateProductModal from "./UpdateProductModal";
import { Link } from "react-router-dom";

function AdminProductsView({ products, setProducts }) {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const handleClose = () => {
    setShowUpdateModal(false);
    setSelectedProduct(null);
  };

  const updateProduct = (product) => {
    setSelectedProduct(product);
    setShowUpdateModal(true);
  };

  return (
    <div className="admin-products-wrapper">
      {/* Admin Hero Section */}
      <div className="admin-hero-section">
        <Container>
          <div className="text-center py-5">
            <h1 className="display-4 fw-bold text-white mb-3">
              <i className="fas fa-cogs me-3"></i>
              Product Management
            </h1>
            <p className="lead text-white-50 mb-4">
              Manage your product inventory with ease and efficiency
            </p>
            <div className="hero-divider mx-auto mb-4"></div>

            {/* Add Product Button */}
            <Button
              as={Link}
              to="/add-product"
              variant="light"
              size="lg"
              className="fw-bold px-4 py-2"
              style={{
                borderRadius: "25px",
                boxShadow: "0 4px 15px rgba(255, 255, 255, 0.2)",
                transition: "all 0.3s ease",
              }}
            >
              <i className="fas fa-plus me-2"></i>
              Add New Product
            </Button>
          </div>
        </Container>
      </div>

      {/* Products Management Section */}
      <Container className="py-5">
        {/* Stats Row */}
        <Row className="mb-5">
          <Col md={4}>
            <Card className="text-center border-0 shadow-sm h-100">
              <Card.Body className="py-4">
                <div className="text-success mb-2">
                  <i className="fas fa-box" style={{ fontSize: "2rem" }}></i>
                </div>
                <h4 className="fw-bold text-success">
                  {products?.length || 0}
                </h4>
                <p className="text-muted mb-0">Total Products</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="text-center border-0 shadow-sm h-100">
              <Card.Body className="py-4">
                <div className="text-primary mb-2">
                  <i className="fas fa-eye" style={{ fontSize: "2rem" }}></i>
                </div>
                <h4 className="fw-bold text-primary">
                  {products?.filter((p) => p.isActive).length || 0}
                </h4>
                <p className="text-muted mb-0">Active Products</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="text-center border-0 shadow-sm h-100">
              <Card.Body className="py-4">
                <div className="text-warning mb-2">
                  <i
                    className="fas fa-eye-slash"
                    style={{ fontSize: "2rem" }}
                  ></i>
                </div>
                <h4 className="fw-bold text-warning">
                  {products?.filter((p) => !p.isActive).length || 0}
                </h4>
                <p className="text-muted mb-0">Inactive Products</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Products Grid */}
        {products && products.length > 0 ? (
          <Row className="g-4">
            {products.map((product) => (
              <Col key={product._id} xs={12} sm={6} md={4} lg={3}>
                <AdminProductCard
                  product={product}
                  updateProduct={updateProduct}
                  setProducts={setProducts}
                />
              </Col>
            ))}
          </Row>
        ) : (
          <div className="text-center py-5">
            <Card className="border-0 shadow-sm">
              <Card.Body className="py-5">
                <i
                  className="fas fa-boxes text-muted mb-3"
                  style={{ fontSize: "3rem" }}
                ></i>
                <h5 className="text-muted">No products found</h5>
                <p className="text-muted mb-3">
                  Start by adding your first product to the inventory
                </p>
                <Button
                  as={Link}
                  to="/add-product"
                  variant="success"
                  className="px-4"
                >
                  <i className="fas fa-plus me-2"></i>
                  Add Product
                </Button>
              </Card.Body>
            </Card>
          </div>
        )}

        {/* Update Product Modal */}
        <UpdateProductModal
          show={showUpdateModal}
          product={selectedProduct}
          setProducts={setProducts}
          handleClose={handleClose}
        />
      </Container>

      {/* Custom Styles */}
      <style jsx>{`
        .admin-products-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }

        .admin-hero-section {
          background: linear-gradient(135deg, #6f42c1 0%, #495057 100%);
          position: relative;
          overflow: hidden;
        }

        .admin-hero-section::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="adminGrain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="white" opacity="0.1"/><circle cx="75" cy="75" r="1" fill="white" opacity="0.1"/><circle cx="50" cy="10" r="0.5" fill="white" opacity="0.15"/><circle cx="90" cy="40" r="0.5" fill="white" opacity="0.15"/><circle cx="10" cy="60" r="0.5" fill="white" opacity="0.15"/></pattern></defs><rect width="100" height="100" fill="url(%23adminGrain)"/></svg>');
          pointer-events: none;
        }

        .hero-divider {
          width: 80px;
          height: 4px;
          background: linear-gradient(90deg, transparent, white, transparent);
          border-radius: 2px;
        }

        .admin-hero-section .btn-light:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(255, 255, 255, 0.3);
        }

        @media (max-width: 768px) {
          .admin-hero-section .display-4 {
            font-size: 2.5rem;
          }

          .admin-hero-section .py-5 {
            padding: 3rem 0 !important;
          }
        }
      `}</style>
    </div>
  );
}

export default AdminProductsView;
